import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  socraticApi,
  socraticReason,
  type SocraticSessionDto,
  type SocraticCoverage,
} from '../../../lib/api/socratic.api';

interface Props {
  tenantId: string;
  studentUid: string;
  assignmentId: string;
  /** The student's UI locale — pinned onto the session at start (decision 12). */
  uiLocale: 'es' | 'en';
  onClose: () => void;
}

type Phase = 'loading' | 'intro' | 'chat' | 'evaluating' | 'result' | 'noAttempts';

/** Humanise a raw axis id into a readable label. STOPGAP: the neutral scaffold
 *  label is not on the evaluation payload (the student cannot read the standard),
 *  so we title-case the id until the payload carries labels. */
function humanizeAxis(axisId: string): string {
  return axisId
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function SocraticDialoguePanel({
  tenantId,
  studentUid,
  assignmentId,
  uiLocale,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation('sis');

  const [phase, setPhase] = useState<Phase>('loading');
  const [session, setSession] = useState<SocraticSessionDto | null>(null);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Load config + resume an in_progress session, or offer to start.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cfg, sessions] = await Promise.all([
          socraticApi.getConfig(tenantId, assignmentId),
          socraticApi.listMySessions(tenantId, assignmentId, studentUid),
        ]);
        if (!alive) return;
        if (cfg) setMaxAttempts(cfg.maxAttempts);
        const inProgress = sessions.find((s) => s.status === 'in_progress');
        const consumed = sessions.filter(
          (s) => s.status === 'closed' || s.status === 'evaluated',
        ).length;
        if (inProgress) {
          setSession(inProgress);
          setPhase('chat');
        } else if (consumed >= (cfg?.maxAttempts ?? 3)) {
          setPhase('noAttempts');
        } else {
          setPhase('intro');
        }
      } catch {
        if (alive) setError(t('portal.socratic.errors.load'));
      }
    })();
    return () => {
      alive = false;
    };
  }, [tenantId, assignmentId, studentUid, t]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [session?.turns.length, phase]);

  const pendingIndex = useMemo(() => {
    if (!session) return -1;
    return session.turns.findIndex((turn) => turn.answer === null);
  }, [session]);

  async function handleStart(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const { session: s } = await socraticApi.start({ tenantId, assignmentId, locale: uiLocale });
      setSession(s);
      setPhase('chat');
    } catch (e) {
      setError(mapError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSend(): Promise<void> {
    if (!session || !answer.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { session: s, closed } = await socraticApi.advance({
        tenantId,
        sessionId: session.id,
        answer: answer.trim(),
      });
      setAnswer('');
      setSession(s);
      if (closed) await runEvaluation(s.id);
    } catch (e) {
      // Infra failure: the answer was NOT persisted, the session stays resumable,
      // no attempt consumed (byblos 01KXMQNW). Keep the typed answer for retry.
      setError(mapError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmClose(): Promise<void> {
    if (!session) return;
    setConfirmClose(false);
    setBusy(true);
    setError(null);
    try {
      const { session: s } = await socraticApi.close({ tenantId, sessionId: session.id });
      setSession(s);
      await runEvaluation(s.id);
    } catch (e) {
      setError(mapError(e));
    } finally {
      setBusy(false);
    }
  }

  async function runEvaluation(sessionId: string): Promise<void> {
    setPhase('evaluating');
    try {
      const { session: s } = await socraticApi.evaluate({ tenantId, sessionId });
      setSession(s);
      setPhase('result');
    } catch (e) {
      // Evaluation failed: the session stays closed + re-evaluable. Surface + retry.
      setError(mapError(e));
      setPhase('chat');
    }
  }

  function mapError(e: unknown): string {
    const reason = socraticReason(e);
    if (reason === 'socratic.no_attempts_left') return t('portal.socratic.errors.noAttempts');
    if (reason === 'socratic.session_in_progress') return t('portal.socratic.errors.alreadyOpen');
    if (reason === 'socratic.locked') return t('portal.socratic.errors.locked');
    // Any infra failure: honest retry, the session is resumable, no attempt spent.
    return t('portal.socratic.errors.infra');
  }

  const attemptsLeft = session ? maxAttempts - session.attemptNumber : maxAttempts;

  return (
    // In-flow (NOT a fixed overlay): the dialogue renders inside the assignment
    // page, so the student answers in context — no fragile modal.
    <Box sx={{ maxWidth: 820, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('portal.socratic.title')}
        </Typography>
        <Button startIcon={<CloseRoundedIcon />} onClick={onClose} color="inherit">
          {t('portal.socratic.exit')}
        </Button>
      </Stack>

      <Box ref={scrollRef}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {phase === 'loading' && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        )}

        {phase === 'intro' && (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body1" color="text.secondary">
              {t('portal.socratic.introBody')}
            </Typography>
            <Button variant="contained" onClick={handleStart} disabled={busy}>
              {busy ? t('portal.socratic.starting') : t('portal.socratic.startButton')}
            </Button>
          </Stack>
        )}

        {phase === 'noAttempts' && (
          <Alert severity="info">{t('portal.socratic.noAttemptsBody')}</Alert>
        )}

        {(phase === 'chat' || phase === 'evaluating') && session && (
          <Stack spacing={2}>
            {session.turns.map((turn) => (
              <Box key={turn.index}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: (th) => alpha(th.palette.primary.main, 0.04) }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {t('portal.socratic.mentor')}
                  </Typography>
                  <Typography variant="body1">{turn.question}</Typography>
                </Paper>
                {turn.answer !== null && (
                  <Paper sx={{ p: 2, mt: 1, ml: { xs: 0, sm: 4 } }} elevation={0}>
                    <Typography variant="overline" color="text.secondary">
                      {t('portal.socratic.you')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {turn.answer}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ))}
            {phase === 'evaluating' && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  {t('portal.socratic.evaluating')}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}

        {phase === 'result' && session?.evaluation && (
          <Stack spacing={3}>
            {/* Prose FIRST — it is the product (decision 16). */}
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {session.evaluation.prose}
            </Typography>
            <Divider />
            {/* Coverage map SECOND — support, not a report card. */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('portal.socratic.coverageTitle')}
              </Typography>
              <Stack spacing={1}>
                {sortCoverage(session.evaluation.coverageMap).map((ax) => (
                  <CoverageRow
                    key={ax.axisId}
                    axisId={ax.axisId}
                    coverage={ax.coverage}
                    {...(ax.label ? { label: ax.label } : {})}
                  />
                ))}
              </Stack>
            </Box>
            <Alert severity={attemptsLeft > 0 ? 'info' : 'warning'}>
              {attemptsLeft > 0
                ? t('portal.socratic.attemptsLeft', { count: attemptsLeft })
                : t('portal.socratic.lastAttempt')}
            </Alert>
            <Button variant="outlined" onClick={onClose}>
              {t('portal.socratic.done')}
            </Button>
          </Stack>
        )}
      </Box>

      {/* Composer (only while chatting, with a pending question) — in-flow below
          the transcript, not pinned to the viewport. */}
      {phase === 'chat' && session && pendingIndex >= 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 2 }}>
          <Stack spacing={1}>
            <TextField
              multiline
              minRows={2}
              maxRows={8}
              fullWidth
              placeholder={t('portal.socratic.answerPlaceholder')}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              disabled={busy}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button color="inherit" onClick={() => setConfirmClose(true)} disabled={busy}>
                {t('portal.socratic.closeButton')}
              </Button>
              <Button
                variant="contained"
                endIcon={<SendRoundedIcon />}
                onClick={handleSend}
                disabled={busy || !answer.trim()}
              >
                {busy ? t('portal.socratic.sending') : t('portal.socratic.send')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Voluntary close confirmation — names the attempt (decision 15). */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>{t('portal.socratic.confirmClose.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('portal.socratic.confirmClose.body', {
              attempt: session?.attemptNumber ?? 1,
              max: maxAttempts,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>
            {t('portal.socratic.confirmClose.cancel')}
          </Button>
          <Button variant="contained" onClick={handleConfirmClose}>
            {t('portal.socratic.confirmClose.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Covered first, pending last (decision 16 — "esto lograste, por aquí sigue"). */
function sortCoverage<T extends { coverage: SocraticCoverage }>(rows: T[]): T[] {
  const order: Record<SocraticCoverage, number> = { covered: 0, partial: 1, untouched: 2 };
  return [...rows].sort((a, b) => order[a.coverage] - order[b.coverage]);
}

function CoverageRow({
  axisId,
  coverage,
  label,
}: {
  axisId: string;
  coverage: SocraticCoverage;
  label?: string;
}): JSX.Element {
  const { t } = useTranslation('sis');
  // Prefer the neutral scaffold label (i18n key); fall back to a humanised axisId.
  const axisName = label ? t(label) : humanizeAxis(axisId);
  // Warm palette, NO traffic light (decision 16). Colour ALWAYS with a label.
  const tone: Record<SocraticCoverage, string> = {
    covered: '#2f6f4f', // warm green-slate
    partial: '#b5852f', // warm amber-brown
    untouched: '#8a7fa6', // muted lavender (never red)
  };
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tone[coverage], flexShrink: 0 }}
      />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {axisName}
      </Typography>
      <Chip
        size="small"
        label={t(`portal.socratic.coverage.${coverage}`)}
        sx={{ bgcolor: alpha(tone[coverage], 0.14), color: tone[coverage], fontWeight: 600 }}
      />
    </Stack>
  );
}
